import { FC, useState } from 'react';
import { Search } from 'lucide-react';
import { Source } from '../mock/types';
import { ChipPopover } from './ChipPopover';

type GoogleAccountsChipProps = {
  sources: Source[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
};

const GoogleLogo: FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox='0 0 24 24' aria-hidden>
    <path fill='#4285F4' d='M21.6 12.227c0-.673-.06-1.32-.173-1.94H12v3.67h5.39a4.61 4.61 0 01-2 3.027v2.516h3.235c1.894-1.745 2.985-4.314 2.985-7.273z' />
    <path
      fill='#34A853'
      d='M12 22c2.7 0 4.964-.895 6.62-2.42l-3.236-2.516c-.897.6-2.04.96-3.384.96-2.605 0-4.81-1.76-5.598-4.124H3.063v2.6A9.997 9.997 0 0012 22z'
    />
    <path fill='#FBBC05' d='M6.402 13.9A6.01 6.01 0 016.078 12c0-.66.116-1.3.324-1.9V7.5H3.063A9.997 9.997 0 002 12c0 1.614.387 3.14 1.063 4.5l3.34-2.6z' />
    <path
      fill='#EA4335'
      d='M12 5.977c1.466 0 2.78.504 3.815 1.49l2.864-2.864C16.96 3.005 14.697 2 12 2 8.094 2 4.71 4.245 3.063 7.5l3.34 2.6C7.19 7.737 9.395 5.977 12 5.977z'
    />
  </svg>
);

export const GoogleAccountsChip: FC<GoogleAccountsChipProps> = ({ sources, selectedIds, onChange }) => {
  const [draft, setDraft] = useState<string[]>(selectedIds);
  const [query, setQuery] = useState('');

  const allGoogle = sources.filter((s) => s.platform === 'google');
  const filteredAll = allGoogle.filter((s) => !query || s.label.toLowerCase().includes(query.toLowerCase()) || s.externalAccountId.includes(query));

  const toggle = (id: string) => {
    setDraft((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const headlineSource = allGoogle.find((s) => selectedIds.includes(s.externalAccountId)) ?? allGoogle[0];

  return (
    <ChipPopover
      icon={<GoogleLogo />}
      label='Google Ads'
      value={headlineSource?.label ?? '—'}
      suffix={<span style={{ color: '#9CA3B1', fontWeight: 400, fontSize: 11.5 }}>· {selectedIds.length} account</span>}
      popWidth={420}
      onOpenChange={(open) => {
        if (open) setDraft(selectedIds);
      }}
    >
      {(close) => (
        <>
          <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid #F0F2F5', position: 'relative' }}>
            <Search size={14} color='#9CA3B1' style={{ position: 'absolute', left: 24, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Cerca per nome o Customer ID…'
              style={{
                width: '100%',
                border: '1px solid #DEE2E8',
                borderRadius: 8,
                padding: '8px 10px 8px 32px',
                fontSize: 13,
                outline: 'none',
                fontFamily: 'inherit',
                color: '#0B1733',
                background: '#F7F8FA',
              }}
            />
          </div>

          <div style={popSecStyle}>Google Ads accounts</div>
          <div style={{ maxHeight: 380, overflowY: 'auto', padding: '0 0 6px' }}>
            {filteredAll.map((s) => {
              const isSelected = draft.includes(s.externalAccountId);
              return (
                <div
                  key={s.externalAccountId}
                  onClick={() => toggle(s.externalAccountId)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 14px',
                    cursor: 'pointer',
                    fontSize: 13,
                    background: isSelected ? '#E8EFFF' : 'transparent',
                  }}
                >
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 4,
                      border: '1.5px solid ' + (isSelected ? '#1452E6' : '#C6CBD4'),
                      background: isSelected ? '#1452E6' : 'transparent',
                      color: 'white',
                      fontSize: 9,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: '0 0 14px',
                    }}
                  >
                    {isSelected ? '✓' : ''}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: isSelected ? '#0F3FBD' : '#0B1733', fontWeight: isSelected ? 600 : 500 }}>{s.label}</span>
                    </div>
                    <div style={cidStyle}>
                      {s.externalAccountId}
                      {s.currency && s.timezone ? (
                        <>
                          {' '}
                          ·{' '}
                          <span style={{ color: '#7A8597' }}>
                            {s.currency} · {s.timezone}
                          </span>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div
            style={{
              borderTop: '1px solid #F0F2F5',
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              fontSize: 12.5,
              color: '#58657B',
            }}
          >
            <span>
              {draft.length} di {allGoogle.length} selezionati
            </span>
            <button
              type='button'
              onClick={() => {
                onChange(draft);
                close();
              }}
              style={btnPrimary}
            >
              Applica
            </button>
          </div>
        </>
      )}
    </ChipPopover>
  );
};

const popSecStyle: React.CSSProperties = {
  padding: '8px 14px 4px',
  fontSize: 10.5,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: '#9CA3B1',
  fontWeight: 600,
};

const cidStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
  fontSize: 11,
  color: '#9CA3B1',
};

const btnPrimary: React.CSSProperties = {
  background: '#1452E6',
  color: 'white',
  border: 0,
  fontFamily: 'inherit',
  padding: '6px 12px',
  borderRadius: 8,
  fontSize: 12.5,
  fontWeight: 600,
  cursor: 'pointer',
};
